/**
 * Centralized download configuration and helpers.
 *
 * Each catalog / Firebase app may have a downloadable file (e.g. an Android APK).
 * The download URL is resolved in this order:
 *   1. app.file_url           — stored on the Firebase "apps" document (admin upload)
 *   2. APP_DOWNLOADS storage_path — Firebase Storage path (admin uploads via admin form)
 *   3. APP_DOWNLOADS download_url — direct / public URL (e.g. /downloads/xxx.apk or external)
 *
 * To make the APK available:
 *   Option A (recommended): Upload via Admin → App Form (stores in Firebase Storage)
 *   Option B: Copy the .apk into frontend/public/downloads/ and set download_url below
 *   Option C: Host externally and set download_url to the public URL
 */

import { getDownloadURL, ref } from "firebase/storage";
import { firebaseStorage, isFirebaseConfigured } from "@/lib/firebase";

/**
 * Download configuration keyed by app_id.
 * Extend this map as new downloadable apps are added.
 */
export const APP_DOWNLOADS = {
  local_android_tax_calculator: {
    // Firebase Storage path — the admin uploads the APK via Admin → App Form.
    // File lands in: premium-app-files/<appId>/<fileName>
    storage_path: "premium-app-files/local_android_tax_calculator/myantaxbox.apk",

    // Fallback direct URL. Uncomment and set if you host the APK in
    // frontend/public/downloads/ or on an external CDN.
    download_url: "/downloads/myantaxbox.apk",

    file_name: "myantaxbox.apk",
    size_mb: 43,
    min_android: "7.0 (API 24)",
    version: "1.0.0",
    build: 2001,
  },
};

/**
 * Installation instructions shown to users after they download.
 * Bilingual (English + Burmese) for accessibility.
 */
export const INSTALL_INFO = {
  requirements: [
    { icon: "✅", text: "Android 7.0 (Nougat) သို့မဟုတ် အသစ်ပိုသည်း", text_en: "Android 7.0 (Nougat) or newer" },
    { icon: "✅", text: "လွတ်နေသော သိုလှောင်မှု ~50 MB", text_en: "~50 MB free storage space" },
  ],
  steps: [
    { text: "သင့်ဖုန်းသို့ .apk ဖိုင်ကို Download ဆွဲပါ။", text_en: "Download the .apk file to your phone." },
    { text: "ဖိုင်ကိုဖွင့်ပါ (Files / Downloads app တွင် နှိပ်ပါ)။", text_en: "Open it (tap the file in your Files / Downloads app)." },
    { text: "'Install unknown apps' ကို ခွင့်ပြုရန် တောင်းဆိုလျှင် ခွင့်ပြုပါ။", text_en: 'If asked, allow "Install unknown apps" for your browser or file manager.' },
    { text: "Install ကိုနှိပ်ပြီးနောက် Open နှိပ်ပါ။", text_en: "Tap Install, then Open." },
  ],
  troubleshooting: [
    {
      icon: "⚠️",
      text: "\"For your security…\" → Settings → Apps → special access → Install unknown apps တွင် ဖွင့်ပါ။",
      text_en: '"For your security…" → Settings → Apps → special access → Install unknown apps.',
    },
  ],
  specs: {
    minAndroid: "7.0 (API 24)",
    targetAndroid: "16 (API 36)",
    version: "1.0.0 (build 2001)",
    recommendedApk: "myantaxbox.apk (~43 MB)",
  },
};

/**
 * Resolve the download URL for an app.
 *
 * @param {object} app — App object (may come from catalog or Firebase)
 * @returns {Promise<{ url: string|null, fileName: string }>}
 */
export async function resolveDownloadUrl(app) {
  const config = APP_DOWNLOADS[app?.app_id] || {};
  const fileName = config.file_name || app?.file_url?.split("/").pop() || "app.apk";

  // 1. Direct file_url from Firebase document
  if (app?.file_url) {
    return { url: app.file_url, fileName };
  }

  // 2. Firebase Storage path from config
  if (config.storage_path && isFirebaseConfigured && firebaseStorage) {
    try {
      const url = await getDownloadURL(ref(firebaseStorage, config.storage_path));
      return { url, fileName };
    } catch (err) {
      console.warn("Failed to resolve Firebase Storage download URL:", err.message);
    }
  }

  // 3. Direct download_url from config
  if (config.download_url) {
    return { url: config.download_url, fileName };
  }

  return { url: null, fileName };
}

/**
 * Trigger a file download in the browser.
 *
 * @param {string} url — The download URL
 * @param {string} fileName — Suggested file name
 */
export function triggerDownload(url, fileName) {
  if (!url) return;
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || "";
  // For cross-origin URLs (Firebase Storage), opening in a new tab
  // lets the browser handle the download instead of navigating away.
  if (url.startsWith("http") && !url.startsWith(window.location.origin)) {
    link.target = "_blank";
    link.rel = "noopener noreferrer";
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Full download flow: resolve URL → trigger download.
 * Returns true if the download was initiated, false otherwise.
 *
 * @param {object} app
 * @returns {Promise<boolean>}
 */
export async function downloadApp(app) {
  const { url, fileName } = await resolveDownloadUrl(app);
  if (!url) {
    return false;
  }
  triggerDownload(url, fileName);
  return true;
}